import { createSelector, defaultMemoize } from 'reselect';
import { List, Map, Record, Set } from 'immutable';
import { CanonicalListItem, filterByLanguage } from 'app/utils/filtering';
import { EDITOR_LANGUAGES } from 'app/constants/enums/Language';
import { ENTITY_PREFIX } from 'app/remote/constants';
import {
  createImmutableEqualSelector,
  chainSelectors,
  memoizeImmutableResult,
} from 'app/utils/redux';
import { seedRandom } from 'app/utils/random';
import { shuffle } from 'app/utils/immutable';
import { isPool, FIB_EMBEDDING_MARKER } from 'app/constants/enums/QuizType';
import { getEntityTypeName } from 'app/utils/schema';
import { findNumOccurrences } from 'app/utils/text';
import { customText, commonText } from 'app/utils/commonCustomText';
import {
  QUESTION_SECTION,
  ARTICLE_SECTION,
  DIALOG_SECTION,
} from 'app/constants/SectionTypes';
import {
  Answer,
  ArticleSection,
  CommonText,
  Curriculum,
  DialogEntry,
  DialogSection,
  DialogSentence,
  DialogSentenceQuiz,
  DialogSpeaker,
  Exercise,
  Issue,
  Lesson,
  Paragraph,
  ParagraphSentence,
  ParagraphSentenceQuiz,
  Question,
  QuestionSection,
  Section,
  ServiceDomain,
  Text,
  VocabSectionWord,
  VocabSectionWordSentence,
  WordTranslation,
} from 'app/constants/schemas';
import {
  makeGetCommonTextTranslations,
  makeGetTextTranslations,
  makeGetVocabSectionWordTranslations,
  makeGetVocabSectionWordSentenceTranslations,
  makeGetPdfTranslations,
  makeFindTranslation,
  makeFilterTranslations,
} from 'app/models/entities/selectors/makeTranslations';
import { STANDARD } from 'app/constants/ExerciseTypes';
import makeFlattenLeaves from './makeFlattenLeaves';
import { unpackVocabSectionWordUnion } from './unionEntity';
import { getAllEntitiesOfType, getEntityOfTypeOrThrow } from './next';

export {
  makeGetCommonTextTranslations,
  makeGetTextTranslations,
  makeGetVocabSectionWordTranslations,
  makeGetVocabSectionWordSentenceTranslations,
  makeGetPdfTranslations,
  makeFindTranslation,
  makeFilterTranslations,
};
export {
  getEntityOfTypeOrThrow,
  getAllEntitiesOfType,
  maybeGetEntityOfType,
} from './next';

export const getEntities = (state) => state.get(ENTITY_PREFIX) || Map();

/**
 * @deprecated use maybeGetEntityOfType or getEntityOfTypeOrThrow
 */
export const getEntityOfType = (state, typeObject, id) =>
  state.getIn([ENTITY_PREFIX, typeObject.storePrefix, id]);

// this is pretty much the same as getEntityOfType but we want to separate it
// to indicate intent
export const getExternalAssociationOfType = (state, typeObject, id) =>
  state.getIn([ENTITY_PREFIX, typeObject.storePrefix, id]);

const makeGetEntity = (typeObject) => (state, id) =>
  getEntityOfType(state, typeObject, id);
const makeGetAllEntities = (typeObject) => (state) =>
  getAllEntitiesOfType(state, typeObject);

// (state, languageId) => Curriculum
export const makeGetCurriculumByLanguage = () => createSelector(
  [
    (state) => getAllEntitiesOfType(state, Curriculum),
    (_, languageId) => languageId,
  ],
  (curricula, languageId) => curricula.find(({ language }) => language === languageId),
);

export const getLessonList = createSelector(
  [state => getAllEntitiesOfType(state, Lesson)], lessons => lessons.toList(),
);

// (state, Lesson) => List(Exercise)
export const makeGetLessonExercises = () => createSelector([
  (state) => getAllEntitiesOfType(state, Exercise),
  (state, lesson) => lesson && lesson.exercises,
],
  (allExercises, exerciseIds) =>
    (exerciseIds || List()).map(id => allExercises.get(id)),
);

export const NumberedExerciseId = Record({ exerciseId: '', exerciseNumber: 0 });

/**
 * @typedef {import('app/types/entity').EntityStringId} EntityStringId
 * @return {(state, lesson) => List<Record<{ exerciseId: EntityStringId; exerciseNumber: number })>>}
 */
export const makeGetNumberedExerciseIds = () => {
  const getLessonExercises = makeGetLessonExercises();
  const getNumberedExercises = createSelector([getLessonExercises],
    lessonExercises => {
      let numStandard = 0;
      let numNonStandard = 0;

      return lessonExercises.map(({ id, exerciseType }) => {
        if (exerciseType === STANDARD.id) {
          numStandard += 1;
          return NumberedExerciseId({ exerciseId: id, exerciseNumber: numStandard });
        } else {
          numNonStandard += 1;
          return NumberedExerciseId({ exerciseId: id, exerciseNumber: numNonStandard });
        }
      });
    },
  );

  // Use deep equality so that the component does not re-render unless the numbered exercise list
  // has actually changed.
  return memoizeImmutableResult(getNumberedExercises);
};

export const SectionGroup = Record({ type: '', sections: List([]) });
export const SectionGroupItem = Record({ id: '', type: '', index: 0 });

export const makeGetSectionGroups = () => {
  const getSections = (state, exerciseId) =>
    getEntityOfTypeOrThrow(state, Exercise, exerciseId).sections.map(
      (sectionId, index) => {
        const section = getEntityOfType(state, Section, sectionId);
        const { id } = section;
        const type = section.schema.typeName;
        return new SectionGroupItem({ id, type, index });
      });

  return createImmutableEqualSelector(
    [getSections],
    sections => (
      sections.size === 0 ? List() :
        sections.reduce(
          (groups, item) => (
            groups.last().type === item.type
              // Append to last group
              ? groups.updateIn([-1, 'sections'], list => list.push(item))
              // Create new group with this item
              : groups.push(new SectionGroup({
                type: item.type,
                sections: List([item]),
              }))),
          // Init with a group for the first item (it will be added on
          // the first iteration
          List([new SectionGroup({ type: sections.first().type })]),
        )
    ));
};

/**
 * Returns a flat list of all quiz entities in the given exercise, in order.
 */
export const makeGetExerciseQuizzes = ({
  getExerciseId = (state, { exerciseId }) => exerciseId,
} = {}) => chainSelectors(
  (state, props) => getEntityOfType(state, Exercise, getExerciseId(state, props)),
  makeFlattenLeaves({ type: Exercise, path: ['sections'] }),
  createSelector(
    [(state, sections) => sections],
    sections => sections.filter(section => {
      const sectionType = section.schema.typeName;

      return (
        sectionType === QUESTION_SECTION.id ||
        sectionType === ARTICLE_SECTION.id ||
        sectionType === DIALOG_SECTION.id
      );
    }),
  ),
  makeFlattenLeaves(
    { type: QuestionSection, path: ['questions'] },
    {
      type: ArticleSection,
      path: ['paragraphs', 'paragraphSentences', 'paragraphSentenceQuizzes'],
    },
    { type: DialogSection, path: ['dialogEntries', 'dialogSentences', 'dialogSentenceQuizzes'] },
  ),
);

export const makeGetDialogSectionQuizIds = ({
  getSectionId = (state, { sectionId }) => sectionId,
} = {}) => chainSelectors(
  (state, props) => getEntityOfType(state, DialogSection, getSectionId(state, props)),
  makeFlattenLeaves({ type: DialogSection, path: ['dialogEntries', 'dialogSentences'] }),
  createSelector(
    [(state, dialogSentences) => dialogSentences],
    (dialogSentences) => dialogSentences.flatMap(sentence => sentence.dialogSentenceQuizzes),
  ),
);

export const makeGetArticleSectionQuizIds = ({
  getSectionId = (state, { sectionId }) => sectionId,
} = {}) => chainSelectors(
  (state, props) => getEntityOfType(state, ArticleSection, getSectionId(state, props)),
  makeFlattenLeaves({ type: ArticleSection, path: ['paragraphs', 'paragraphSentences'] }),
  createSelector(
    [(state, paragraphSentences) => paragraphSentences],
    (paragraphSentences) =>
      paragraphSentences.flatMap(sentence => sentence.paragraphSentenceQuizzes),
  ),
);

export const makeGetQuizIndex = () => createSelector(
  [makeGetExerciseQuizzes(), (state, { quizId }) => quizId],
  (exerciseQuizzes, quizId) =>
    exerciseQuizzes.findIndex(quiz => quiz.id === quizId),
);

export const makeGetQuizPrompts = () => createSelector(
  [
    (state, { quizType }) => getAllEntitiesOfType(state, quizType),
    (state, { quizIds }) => quizIds,
  ],
  (allQuizzes, quizIds) => quizIds.map(id => allQuizzes.get(id).prompt),
);

export const findPreviousQuizInExercise = (state, { exerciseId, quizId }) => {
  const getExerciseQuizzes = makeGetExerciseQuizzes();
  const exerciseQuizzes = getExerciseQuizzes(state, { exerciseId });

  const quizIndex = exerciseQuizzes.findIndex(quiz => quiz.id === quizId);
  const previousQuiz = exerciseQuizzes.get(quizIndex - 1);

  return previousQuiz || undefined;
};

export const makeGetPooledAnswerQuizzesBefore = () => createSelector(
  [
    makeGetExerciseQuizzes(),
    (state, { quizId }) => quizId,
    (state, { quizEntityType }) => quizEntityType,
  ],
  (exerciseQuizzes, quizId, quizEntityType) => (
    exerciseQuizzes
      .reverse()
      .skipUntil(quiz => quiz.id === quizId && getEntityTypeName(quiz) === quizEntityType)
      .takeWhile(quiz => isPool(quiz.quizType))
      .reverse()
  ),
);

export const makeShufflePooledAnswerIds = () => createSelector(
  [
    // Use the ID of the first answer in the pool as the random seed, so that the randomized order
    // of the pool is consistent. Use the cohort ID so the order stays the same when the lesson is
    // published vs.  unpublished (since a published lesson is basically a copy of the master
    // lesson, with independent entities).
    (state, { answerIds }) => (
      // Don't need to shuffle if there is only one answer
      answerIds.size > 1 ? getEntityOfType(state, Answer, answerIds.first()).cohortId : undefined
    ),
    (state, { answerIds }) => answerIds,
  ],
  (randomSeed, answerIds) => {
    if (randomSeed) {
      const randomFunction = seedRandom(randomSeed);

      return shuffle(answerIds, randomFunction);
    } else {
      return answerIds;
    }
  },
);

export const makeGetIsQuizLastInPool = () => createSelector(
  [
    makeGetExerciseQuizzes(),
    (state, { quizId }) => quizId,
    (state, { quizEntityType }) => quizEntityType,
  ],
  (exerciseQuizzes, quizId, quizEntityType) => {
    const quizIndex = exerciseQuizzes.findIndex(quiz =>
      getEntityTypeName(quiz) === quizEntityType && quiz.id === quizId);

    const currentQuiz = exerciseQuizzes.get(quizIndex);
    const nextQuiz = exerciseQuizzes.get(quizIndex + 1);

    return isPool(currentQuiz.quizType) && (!nextQuiz || !isPool(nextQuiz.quizType));
  },
);

export const getDialogEntryIsStageDirection = (state, id) =>
  Boolean(getEntityOfTypeOrThrow(state, DialogEntry, id).stageDirection);

export const getDialogSpeakerName = (state, id) => (
  id ? getEntityOfTypeOrThrow(state, DialogSpeaker, id).name : ''
);
export const getDialogSpeakerLanguage = (state, id) => (
  id ? getEntityOfTypeOrThrow(state, DialogSpeaker, id).language : ''
);
export const makeGetDialogSpeakersInLanguage = () => createSelector(
  [
    (state) => getAllEntitiesOfType(state, DialogSpeaker),
    (state, { targetLanguage }) => targetLanguage,
  ],
  filterByLanguage,
);
export const makeGetCanonicalDialogSpeakers = () => createSelector(
  [
    makeGetDialogSpeakersInLanguage(),
    makeGetAllDialogSpeakerIdsInLesson(),
  ],
  (allSpeakers, allSpeakersInLesson) => {
    const isSpeakerInLesson = (speakerId) => allSpeakersInLesson.has(speakerId);

    return allSpeakers
      .map(({ id, name, nameTransliteration }) =>
        // We concatenate nameTransliteration and name to influence searching.
        // This does not actually change display but affects filtering and
        // sorting. We put the transliteration first because we want to order
        // by that first and then fallback to name if not present.
        new CanonicalListItem({ id, text: `${nameTransliteration || ''}${name}` }),
      )
      .toList()
      // Put speakers already used in lesson and at the top of the list.
      .sortBy(listItem => (isSpeakerInLesson(listItem.id) ? -1 : 0));
  },
);

export const makeGetAllDialogSpeakerIdsInLesson = () => chainSelectors(
  (state, { lessonId }) => getEntityOfType(state, Lesson, lessonId),
  makeFlattenLeaves({ type: Lesson, path: ['exercises', 'sections'] }),
  createSelector(
    [(state, sections) => sections],
    (sections) => sections.filter(section => section.schema.typeName === DIALOG_SECTION.id),
  ),
  makeFlattenLeaves({ type: DialogSection, path: ['dialogEntries'] }),
  createSelector(
    [(state, dialogEntries) => dialogEntries],
    (dialogEntries) => Set(dialogEntries.map(entry => entry.dialogSpeaker)),
  ),
);

export const getAllParagraphs = makeGetAllEntities(Paragraph);
export const getAllParagraphSentenceQuizzes = makeGetAllEntities(ParagraphSentenceQuiz);
export const getAllDialogSentences = makeGetAllEntities(DialogSentence);
export const getAllDialogSentenceQuizzes = makeGetAllEntities(DialogSentenceQuiz);
export const getParagraphSentence = makeGetEntity(ParagraphSentence);
export const getParagraphSentenceQuiz = makeGetEntity(ParagraphSentenceQuiz);

// Gets the IDs of the correct answers for the given quiz.
export const makeGetQuizCorrectAnswerIds = getQuizEntity => createSelector(
  [getQuizEntity, (state) => getAllEntitiesOfType(state, Answer)],
  (quiz, allAnswers) =>
    quiz.answers
    .map(answerId => allAnswers.get(answerId))
    .filter(answer => answer.correct)
    .map(answer => answer.id),
);

export const makeGetNumQuizMarkers = (getTextSelector) => createSelector(
  [getTextSelector],
  (text) => findNumOccurrences(text, FIB_EMBEDDING_MARKER),
);

export const makeGetCommonTextsCanonicalList = () => createSelector(
  [state => getAllEntitiesOfType(state, CommonText)], commonTexts => commonTexts.toList(),
);

export const makeGetCommonTextsInLanguage = () => createSelector(
  [makeGetCommonTextsCanonicalList(), (state, options) => options],
  filterByLanguage,
);

export const makeGetVocabWords = () => createSelector(
  [(state, id) => getEntityOfType(state, Section, id), state => getAllEntitiesOfType(state, VocabSectionWord)],
  ({ vocabSectionWords: words }, allWords) => words.map(id => allWords.get(id)),
);

/**
 * Helper for getting a text field's string value directly. Always returns a string, and returns an
 * empty string if any of the fields are undefined.
 *
 * @returns {string}
 */
export const getTextFieldString = (state, entity, field) => {
  if (!entity) {
    return '';
  }

  const { id, schema: type } = entity;
  const text = findCustomOrCommonText(state, { field, id, type });
  return text ? text.text : '';
};

/**
 * Finds the appropriate text for a custom or common text field.
 *
 * @param {string} options.field - The field name or suffix of the field name.
 * @returns {CommonText|Text|undefined}
 */
export const findCustomOrCommonText = (state, { field, id, type }) => {
  const entity = getEntityOfType(state, type, id);

  const custom = customText(entity, field);
  const common = commonText(entity, field);

  return custom.selected ? getEntityOfType(state, Text, custom.id) :
         common.selected ? getEntityOfType(state, CommonText, common.id) :
         undefined;
};

export const makeFindCustomOrCommonTextTranslation = () => {
  const getTextTranslations = makeGetTextTranslations();
  const getCommonTextTranslations = makeGetCommonTextTranslations();
  const findTranslation = makeFindTranslation();

  /**
   * @returns {TextTranslation|undefined}
   */
  return (state, { id, type, field, displayLanguage, showTranslations }) => {
    const entity = getEntityOfType(state, type, id);

    const custom = customText(entity, field);
    const common = commonText(entity, field);

    const translations = (
      custom.id ? getTextTranslations(state, custom.id) :
      common.id ? getCommonTextTranslations(state, common.id) :
      []
    );

    const translation = findTranslation(
      translations,
      showTranslations,
      displayLanguage,
    );

    return translation;
  };
};

export const makeGetAllWordTranslations = () => createSelector(
  [
    (state, id) => unpackVocabSectionWordUnion(state, { id }),
    (state) => getAllEntitiesOfType(state, WordTranslation),
  ],
  (word, wordTranslations) => word.translations.map(id => wordTranslations.get(id)),
);

export const makeAreWordTranslationsEmpty = () => createSelector(
  [(state, id) => getEntityOfType(state, VocabSectionWord, id), makeGetAllWordTranslations()],
  (word, wordTranslations) => (
    word.translations.isEmpty() ||
    wordTranslations.every(translation => !translation.translation)
  ),
);

export const isSentenceEmpty = (state, sentence) => (
  !sentence.sound &&
  !sentence.image &&
  !getTextFieldString(state, sentence, 'text')
  // We don't need to check the sentence text's TextTranslation because there should be no
  // translations for empty texts.
);

export const makeAreSentencesEmpty = () => createSelector(
  [
    (state, id) => getEntityOfType(state, VocabSectionWord, id),
    (state) => getAllEntitiesOfType(state, VocabSectionWordSentence),
  ],
  (word, allSentences) => {
    if (!word.vocabSectionWordSentences || word.vocabSectionWordSentences.isEmpty()) {
      return true;
    }

    const sentences = word.vocabSectionWordSentences.map(id => allSentences.get(id));

    return sentences.every(sentence =>
      !sentence.sound &&
      !sentence.image &&
      !sentence.text &&
      !sentence.transliteration,
      // We don't need to check the sentence text's exampleSentenceTranslations because there
      // should be no translations for empty texts.
    );
  },
);

export const makeIsWordEmpty = () => createSelector(
  [
    (state, id) => getEntityOfType(state, VocabSectionWord, id),
    makeAreWordTranslationsEmpty(),
    makeAreSentencesEmpty(),
  ],
  (word, translationsEmpty, exampleSentencesEmpty) => (
    !word.pronunciation &&
    !word.transliteration &&
    !word.partOfSpeech &&
    !word.definition &&
    !word.sound &&
    translationsEmpty &&
    exampleSentencesEmpty
  ),
);

export const isParagraphSentenceEmpty = (state, id) => {
  const sentence = getParagraphSentence(state, id);

  return (
    isSentenceEmpty(state, sentence) &&
    sentence.paragraphSentenceQuizzes.every(quizId =>
      isQuizEmpty(state, getParagraphSentenceQuiz(state, quizId)))
  );
};

export const isParagraphEmpty = (state, id) => {
  const paragraph = getEntityOfType(state, Paragraph, id);

  return (
    !paragraph.leadingMediaGroup &&
    !paragraph.trailingMediaGroup &&
    paragraph.paragraphSentences.every(sentenceId =>
      isParagraphSentenceEmpty(state, sentenceId))
  );
};

export const isDialogSentenceEmpty = (state, id) => {
  const sentence = getEntityOfType(state, DialogSentence, id);

  return (
    isSentenceEmpty(state, sentence) &&
    sentence.dialogSentenceQuizzes.every(quizId =>
      isQuizEmpty(state, getEntityOfType(state, DialogSentenceQuiz, quizId)))
  );
};

export const isDialogEntryEmpty = (state, id) => {
  const entry = getEntityOfType(state, DialogEntry, id);

  return (
    !entry.dialogSpeaker &&
    entry.dialogSentences.every(sentenceId => isDialogSentenceEmpty(state, sentenceId))
  );
};

export const isQuestionEmpty = (state, id) => {
  const question = getEntityOfType(state, Question, id);

  // Treats a question as "empty" if it has no text and no answers. Ignores text translations.
  return (
    !getTextFieldString(state, question, 'text') &&
    isQuizEmpty(state, question)
  );
};

export const isQuizEmpty = (state, quizEntity) => (
  !getTextFieldString(state, quizEntity, 'teacherNoteText') &&
  quizEntity.answers.every(answerId =>
    !getTextFieldString(state, getEntityOfType(state, Answer, answerId), 'text'))
);

// In single language mode show only current language
// Show languages that are don't have translations for yet
export const makeFilterAdditiveLanguages = () => defaultMemoize(
  (translations, singleLanguageMode, displayLanguage, targetLanguage, t) =>
    EDITOR_LANGUAGES
      .filter(language => language !== targetLanguage)
      .filter(language => (singleLanguageMode ? language === displayLanguage : true))
      .filter(userLanguage => !translations.find(({ language }) => language === userLanguage))
      .map((language) => new CanonicalListItem({
        id: language,
        text: language.getName(t),
        language,
      })),
);

/**
 * @param {Map} state
 * @param {import('app/types/entity').IEntity} type
 * @param {string} id
 * @param {string} field
 * @return {List<Issue>}
 */
export const getIssuesByType = (state, type, id, field) => {
  /**
   * @param {Issue} a
   * @param {Issue} b
   */
  const comparator = (a, b) => {
    // If the createdAt date is undefined, it means the issue has not been created on the server,
    // but means it will be at the top after it has been added to the database. Treat it as recent.
    if (a.createdAt === b.createdAt) {
      return 0;
    } else if (a.createdAt === undefined || a.createdAt > b.createdAt) {
      return -1;
    } else {
      return 1;
    }
  };

  const entity = getEntityOfType(state, type, id);
  const issues = entity ? entity.get(field) : [];

  return issues.map(issue => getEntityOfType(state, Issue, issue)).sort(comparator);
};

/**
 * Gets a map of service domains by service.
 *
 * @param {Map} state
 * @returns {Map}
 */
export const getServiceDomainsByService = createSelector(
  (state) => getAllEntitiesOfType(state, ServiceDomain),
  (serviceDomains) =>
    serviceDomains
      .groupBy((serviceDomain) => serviceDomain.service)
      .map((serviceDomainGroup) => serviceDomainGroup.sortBy(({ default: d }) => d).toList()),
);
